import { useState, useEffect } from 'react';

const FORM_KEY = 'codeliance_contact_submitted';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    phone: ''
  });
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const blocked = localStorage.getItem(FORM_KEY);
    if (blocked) {
      setIsBlocked(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isBlocked) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          phone: formData.phone
        }),
      });

      if (response.ok) {
        localStorage.setItem(FORM_KEY, 'true');
        setIsBlocked(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-16 sm:py-24 lg:py-32 bg-[#050608] relative overflow-hidden">
      {/* Iluminación de fondo */}
      <div className="absolute top-0 left-1/4 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-[#1A73E8]/5 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bg-[#00C2FF]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 lg:gap-20 items-center">
          
          {/* Columna Izquierda: Información */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-md mb-6 sm:mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00C2FF] animate-pulse"></span>
              <span className="text-white text-[11px] font-bold uppercase tracking-[0.2em]">Contacto</span>
            </div>

            <h2 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.1] mb-5 sm:mb-8">
              ¿Listo para dar el <br /> 
              <span className="text-[#00C2FF]">siguiente paso?</span>
            </h2>

            <p className="text-base sm:text-lg lg:text-xl text-[#86868b] leading-relaxed mb-8 sm:mb-12 max-w-lg">
              Hablemos sobre cómo podemos eliminar la fricción en tus procesos y escalar tu visión con tecnología de vanguardia. ¿Necesitas desarrollar software a medida? ¿Quieres implementar inteligencia artificial en tu negocio? Contáctanos hoy para una consulta gratuita.
            </p>

            <div className="space-y-6 sm:space-y-8">
              <div className="flex items-center gap-4 sm:gap-6 group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 flex items-center justify-center rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 group-hover:border-[#00C2FF]/40 transition-all duration-500">
                  <i className="ri-phone-line text-xl sm:text-2xl text-[#00C2FF]"></i>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black text-[#86868b] uppercase tracking-widest mb-1">Teléfono</div>
                  <a href="tel:+573017400570" className="text-white font-bold text-base sm:text-lg hover:text-[#00C2FF] transition-colors">
                    +57 301 740 0570
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 sm:gap-6 group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 flex items-center justify-center rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 group-hover:border-[#00C2FF]/40 transition-all duration-500">
                  <i className="ri-mail-line text-xl sm:text-2xl text-[#00C2FF]"></i>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black text-[#86868b] uppercase tracking-widest mb-1">Email</div>
                  <a href="mailto:comercial@codeliance.co" className="text-white font-bold text-sm sm:text-lg hover:text-[#00C2FF] transition-colors break-all sm:break-normal">
                    comercial@codeliance.co
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha: Formulario */}
          <div className="p-5 sm:p-8 lg:p-10 rounded-[2rem] sm:rounded-[2.5rem] lg:rounded-[3rem] bg-[#0d1117]/80 border border-white/5 backdrop-blur-xl shadow-2xl relative">
            {isBlocked ? (
              <div className="py-12 sm:py-16 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00C2FF]/10 border border-[#00C2FF]/20 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-[#00C2FF]"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">¡Mensaje enviado!</h3>
                <p className="text-[#86868b] text-sm sm:text-base max-w-sm mx-auto">
                  Gracias por contactarnos. Un miembro de nuestro equipo te contactará pronto.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-[#86868b] uppercase ml-2 tracking-widest">Nombre</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                      required
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#050608] border border-white/5 rounded-xl sm:rounded-2xl text-white text-sm sm:text-base focus:outline-none focus:border-[#00C2FF]/50 transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-[#86868b] uppercase ml-2 tracking-widest">Empresa</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#050608] border border-white/5 rounded-xl sm:rounded-2xl text-white text-sm sm:text-base focus:outline-none focus:border-[#1A73E8]/50 transition-all"
                      placeholder="Compañía"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-[#86868b] uppercase ml-2 tracking-widest">Teléfono</label>
                  <input
                    type="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                    required
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#050608] border border-white/5 rounded-xl sm:rounded-2xl text-white text-sm sm:text-base focus:outline-none focus:border-[#00C2FF]/50 transition-all"
                    placeholder="301 740 0570"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-[#86868b] uppercase ml-2 tracking-widest">Email Corporativo</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                    required
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#050608] border border-white/5 rounded-xl sm:rounded-2xl text-white text-sm sm:text-base focus:outline-none focus:border-[#00C2FF]/50 transition-all"
                    placeholder="ejemplo@empresa.com"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-[#86868b] uppercase ml-2 tracking-widest">Mensaje</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                    required
                    rows={4}
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#050608] border border-white/5 rounded-xl sm:rounded-2xl text-white text-sm sm:text-base focus:outline-none focus:border-[#00C2FF]/50 transition-all resize-none"
                    placeholder="Cuéntanos sobre tu proyecto..."
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-[#050608] text-[#00C2FF] focus:ring-[#00C2FF]/50"
                  />
                  <label htmlFor="consent" className="text-xs text-gray-400 leading-relaxed">
                    Acepto la <a href="/politica-privacidad" target="_blank" className="text-[#00C2FF] hover:underline">Política de Privacidad</a> y autorizo el tratamiento de mis datos personales según la Ley 1581 de 2012.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !consent}
                  className="w-full py-4 sm:py-5 bg-white hover:bg-[#00C2FF] text-black hover:text-white font-black uppercase tracking-tighter text-base sm:text-lg rounded-xl sm:rounded-2xl transition-all duration-500 shadow-xl flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Procesando...' : 'Enviar Propuesta'}
                  <i className="ri-arrow-right-up-line text-2xl"></i>
                </button>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}